import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { BedDouble, Ruler, MapPin, Car, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Card = ({ p, i }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
  >
    <Link
      to={`/imovel/${p.id}`}
      className="group block overflow-hidden rounded-b-lg bg-white shadow-sm transition-shadow duration-500 hover:shadow-xl"
      data-testid={`property-card-${p.id}`}
    >
      <div className="relative max-h-[300px] overflow-hidden">
        <img
          src={p.images[0]}
          alt={p.title}
          loading="lazy"
          className="h-[260px] w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {(p.status === "reservado" || p.status === "vendido") && (
          <span
            className={`absolute left-4 top-4 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider ${
              p.status === "vendido" ? "bg-seed-deep text-seed-off" : "bg-seed-vivid text-seed-deep"
            }`}
            data-testid={`property-status-${p.id.slice(0, 8)}`}
          >
            {p.status === "vendido" ? "Vendido" : "Reservado"}
          </span>
        )}
        <span className="absolute right-4 top-4 flex items-center gap-1 rounded-full bg-seed-deep/85 px-4 py-1.5 text-xs font-bold text-seed-off opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
          Ver detalhes <ArrowUpRight size={13} />
        </span>
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold tracking-tight text-seed-deep">{p.title}</h3>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-seed-deep/60">
              <MapPin size={14} className="text-seed-salad" /> {p.location}
            </p>
          </div>
          <span className="whitespace-nowrap font-heading text-lg font-black text-seed-salad">{p.price}</span>
        </div>
        <div className="mt-5 flex items-center gap-5 border-t border-seed-deep/10 pt-4 text-xs font-semibold text-seed-deep/70">
          <span className="flex items-center gap-1.5"><BedDouble size={15} className="text-seed-salad" /> {p.beds} quartos</span>
          <span className="flex items-center gap-1.5"><Ruler size={15} className="text-seed-salad" /> {p.area}</span>
          <span className="flex items-center gap-1.5"><Car size={15} className="text-seed-salad" /> {p.garage} vagas</span>
        </div>
      </div>
    </Link>
  </motion.div>
);

export const Properties = () => {
  const [tab, setTab] = useState("urbano");
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [minBeds, setMinBeds] = useState("0");
  const [priceRange, setPriceRange] = useState("all");

  useEffect(() => {
    axios
      .get(`${API}/properties`)
      .then((r) => setProperties(r.data))
      .catch(() => setProperties([]))
      .finally(() => setLoading(false));
  }, []);

  const parsePrice = (p) => Number((p || "").replace(/\D/g, "")) || 0;

  const list = properties
    .filter((p) => p.type === tab)
    .filter((p) => `${p.title} ${p.location}`.toLowerCase().includes(search.toLowerCase()))
    .filter((p) => (p.beds || 0) >= Number(minBeds))
    .filter((p) => {
      const v = parsePrice(p.price);
      if (priceRange === "ate500") return v < 500000;
      if (priceRange === "500a1m") return v >= 500000 && v <= 1000000;
      if (priceRange === "acima1m") return v > 1000000;
      return true;
    });

  return (
    <section id="imoveis" className="bg-white py-24 md:py-36" data-testid="properties-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-14 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
        >
          <div className="max-w-xl">
            <p className="mb-4 text-sm font-bold uppercase tracking-[0.25em] text-seed-salad">Portfólio</p>
            <h2 className="font-heading text-4xl font-black tracking-tighter text-seed-deep sm:text-5xl" data-testid="properties-title">
              Imóveis em destaque
            </h2>
          </div>
          <div className="flex rounded-full bg-seed-off p-1.5" data-testid="properties-tabs">
            {[
              { key: "urbano", label: "Urbanos", testId: "tab-urbanos" },
              { key: "rural", label: "Rurais", testId: "tab-rurais" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={t.testId}
                className={`rounded-full px-7 py-2.5 font-heading text-sm font-bold transition-all duration-300 ${
                  tab === t.key ? "bg-seed-salad text-white shadow-md" : "text-seed-deep/60 hover:text-seed-deep"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="mt-8 flex w-full flex-col gap-3 sm:flex-row" data-testid="properties-filters">
            <input
              placeholder="Buscar por nome ou cidade..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="filter-search"
              className="h-12 flex-1 rounded-full bg-seed-off px-6 text-sm text-seed-deep outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]"
            />
            <select value={minBeds} onChange={(e) => setMinBeds(e.target.value)} data-testid="filter-beds" className="h-12 rounded-full bg-seed-off px-5 text-sm font-semibold text-seed-deep outline-none">
              <option value="0">Quartos: todos</option>
              <option value="2">2+ quartos</option>
              <option value="3">3+ quartos</option>
              <option value="4">4+ quartos</option>
            </select>
            <select value={priceRange} onChange={(e) => setPriceRange(e.target.value)} data-testid="filter-price" className="h-12 rounded-full bg-seed-off px-5 text-sm font-semibold text-seed-deep outline-none">
              <option value="all">Preço: todos</option>
              <option value="ate500">Até R$ 500 mil</option>
              <option value="500a1m">R$ 500 mil a R$ 1 mi</option>
              <option value="acima1m">Acima de R$ 1 mi</option>
            </select>
          </div>
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="properties-loading">
            {[0, 1, 2].map((n) => (
              <div key={n} className="h-[380px] animate-pulse rounded-b-lg bg-seed-deep/5" />
            ))}
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="properties-grid">
            <AnimatePresence mode="popLayout">
              {list.map((p, i) => (
                <Card key={p.id} p={p} i={i} />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
        {!loading && list.length === 0 && (
          <p className="py-16 text-center text-seed-deep/50" data-testid="properties-empty">
            Nenhum imóvel encontrado com esses filtros.
          </p>
        )}
      </div>
    </section>
  );
};
